import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge, TypeBadge } from '@/components/common/StatusBadge';
import { Droplets, Search, FileSignature, FileText, ArrowUpDown, ArrowUp, ArrowDown, Eye, Pencil, Lock } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Permits Registry — global ledger of every filled-out form (form_instance)
 * across all projects/jobs. ONE row per form, NOT per job. Replaces the
 * old "Jobs filtered by type=permits" view.
 *
 * Columns: Job/Project (+customer) · Form Type · Form Status · Date · Assigned
 * Sortable by column. Filterable by form type and global search.
 * Signed forms open as read-only PDF; pending forms open the normal flow.
 */

const FORM_STATUS_DISPLAY = {
  draft: { label: 'Pending', color: 'amber' },
  in_progress: { label: 'Pending', color: 'amber' },
  completed: { label: 'Pending', color: 'amber' }, // filled but not signed
  filled: { label: 'Pending', color: 'amber' },
  viewed: { label: 'Pending', color: 'amber' },
  sent: { label: 'Pending', color: 'amber' },
  signed: { label: 'Signed', color: 'emerald' },
};

function getFormDisplayStatus(inst) {
  // Prefer explicit signed_at over status field (more reliable).
  if (inst.signed_at) return 'signed';
  const s = (inst.status || 'draft').toLowerCase();
  return s === 'signed' ? 'signed' : 'pending';
}

export default function PermitsRegistryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [instances, setInstances] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      api.get('/custom-forms/instances').catch(() => ({ data: [] })),
      api.get('/custom-forms/templates').catch(() => ({ data: [] })),
      api.get('/jobs', { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
      api.get('/projects').catch(() => ({ data: [] })),
      api.get('/contacts', { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
      api.get('/auth/users').catch(() => ({ data: [] })),
    ]).then(([instRes, tplRes, jobRes, projRes, contRes, userRes]) => {
      if (!active) return;
      setInstances(instRes.data || []);
      setTemplates(tplRes.data || []);
      setJobs(jobRes.data?.items || jobRes.data || []);
      setProjects(projRes.data || []);
      setContacts(contRes.data?.items || contRes.data || []);
      setUsers(userRes.data || []);
    }).catch(() => toast.error(t('errors.loadingError')))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [t]);

  // Build lookups
  const jobById = useMemo(() => Object.fromEntries(jobs.map(j => [j.id, j])), [jobs]);
  const projectById = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);
  const contactById = useMemo(() => Object.fromEntries(contacts.map(c => [c.id, c])), [contacts]);
  const userById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);
  const templateById = useMemo(() => Object.fromEntries(templates.map(tpl => [tpl.id, tpl])), [templates]);

  // Resolve helpers
  const resolveContext = (inst) => {
    let job = null, project = null, customerName = '';
    if (inst.linked_type === 'job') {
      job = jobById[inst.linked_id];
      if (job?.project_id) project = projectById[job.project_id];
    } else if (inst.linked_type === 'project') {
      project = projectById[inst.linked_id];
    }
    const customerId = job?.customer_id || project?.customer_id;
    if (customerId) {
      const c = contactById[customerId];
      if (c) customerName = `${c.first_name || ''} ${c.last_name || ''}`.trim();
    }
    const jobOrProjectNumber = job?.job_number || project?.project_number || '';
    const jobOrProjectTitle = job?.title || project?.title || '';
    const tpl = templateById[inst.template_id] || {};
    const formType = inst.template_name || tpl.name || 'Unknown';

    // Assignees: prefer the job's assigned team; otherwise the form's created_by.
    const assignees = [];
    if (job?.team_members?.length) {
      job.team_members.forEach(m => assignees.push(m.name || userById[m.user_id]?.name || ''));
    } else if (inst.created_by) {
      assignees.push(userById[inst.created_by]?.name || '');
    }

    return {
      job,
      project,
      jobOrProjectNumber,
      jobOrProjectTitle,
      customerName,
      formType,
      assignees: assignees.filter(Boolean),
    };
  };

  // Filtering + sorting
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return instances.filter(inst => {
      const ctx = resolveContext(inst);
      if (typeFilter !== 'all' && inst.template_id !== typeFilter) return false;
      if (statusFilter !== 'all') {
        const ds = getFormDisplayStatus(inst);
        if (ds !== statusFilter) return false;
      }
      if (!q) return true;
      return [
        ctx.formType, ctx.jobOrProjectNumber, ctx.jobOrProjectTitle,
        ctx.customerName, ...(ctx.assignees || []),
      ].some(v => (v || '').toLowerCase().includes(q));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instances, search, typeFilter, statusFilter, jobs, projects, contacts, users, templates]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const ca = resolveContext(a);
      const cb = resolveContext(b);
      let va, vb;
      switch (sortBy) {
        case 'job': va = ca.jobOrProjectNumber; vb = cb.jobOrProjectNumber; break;
        case 'customer': va = ca.customerName; vb = cb.customerName; break;
        case 'type': va = ca.formType; vb = cb.formType; break;
        case 'status': va = getFormDisplayStatus(a); vb = getFormDisplayStatus(b); break;
        case 'assigned': va = (ca.assignees[0] || ''); vb = (cb.assignees[0] || ''); break;
        case 'created_at':
        default: va = a.signed_at || a.filled_at || a.created_at || ''; vb = b.signed_at || b.filled_at || b.created_at || ''; break;
      }
      return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
    return arr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('asc'); }
  };
  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="w-3 h-3 text-gray-300 inline-block ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 text-[#003366] inline-block ml-1" />
      : <ArrowDown className="w-3 h-3 text-[#003366] inline-block ml-1" />;
  };

  const openInstance = async (inst) => {
    const isSigned = getFormDisplayStatus(inst) === 'signed';
    if (isSigned) {
      // Read-only: open the rendered PDF in a new tab.
      try {
        const res = await api.get(`/custom-forms/instances/${inst.id}/pdf`, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        window.open(url, '_blank', 'noopener');
      } catch {
        toast.error(t('errors.loadingError'));
      }
      return;
    }
    // Pending: open the normal form-filler flow.
    const ctx = resolveContext(inst);
    if (ctx.job?.id) navigate(`/jobs/${ctx.job.id}`);
    else if (ctx.project?.id) navigate(`/projects/${ctx.project.id}`);
    else toast.info('Open the linked job/project from the registry below.');
  };

  return (
    <div data-testid="permits-registry-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <Droplets className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" />
            Permits — Form Registry
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            {sorted.length} {sorted.length === 1 ? 'form' : 'forms'} ·
            <span className="ml-1 text-gray-400">all permit, NOC, completion report & credit card forms across projects</span>
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="search-permits-registry"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by form, customer, job, project..."
            className="pl-10 h-9 bg-white"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="filter-form-type" className="h-9 w-full sm:w-64 bg-white"><SelectValue placeholder="All form types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All form types</SelectItem>
            {templates.map(tpl => (
              <SelectItem key={tpl.id} value={tpl.id}>{tpl.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="filter-form-status" className="h-9 w-full sm:w-44 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="signed">Signed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Desktop Table */}
      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden hidden md:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
          ) : sorted.length === 0 ? (
            <div className="py-12 text-center">
              <FileSignature className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-gray-500 font-medium">No forms yet</p>
              <p className="text-sm text-gray-400 mt-1">Attach a form from any project or job to see it here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th onClick={() => toggleSort('job')} className="text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2 cursor-pointer select-none hover:text-[#003366]" data-testid="sort-job">Job / Project<SortIcon col="job" /></th>
                    <th onClick={() => toggleSort('customer')} className="text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2 cursor-pointer select-none hover:text-[#003366]" data-testid="sort-customer">Customer<SortIcon col="customer" /></th>
                    <th onClick={() => toggleSort('type')} className="text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2 cursor-pointer select-none hover:text-[#003366]" data-testid="sort-type">Form Type<SortIcon col="type" /></th>
                    <th onClick={() => toggleSort('status')} className="text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2 cursor-pointer select-none hover:text-[#003366]" data-testid="sort-status">Form Status<SortIcon col="status" /></th>
                    <th onClick={() => toggleSort('created_at')} className="text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2 cursor-pointer select-none hover:text-[#003366]" data-testid="sort-date">Date<SortIcon col="created_at" /></th>
                    <th onClick={() => toggleSort('assigned')} className="text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2 cursor-pointer select-none hover:text-[#003366]" data-testid="sort-assigned">Assigned<SortIcon col="assigned" /></th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(inst => {
                    const ctx = resolveContext(inst);
                    const displayStatus = getFormDisplayStatus(inst);
                    const isSigned = displayStatus === 'signed';
                    const date = (inst.signed_at || inst.filled_at || inst.created_at || '').slice(0, 10);
                    return (
                      <tr
                        key={inst.id}
                        data-testid={`form-row-${inst.id}`}
                        onClick={() => openInstance(inst)}
                        className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors cursor-pointer"
                      >
                        <td className="px-3 py-2">
                          <p className="text-xs font-mono text-gray-500">{ctx.jobOrProjectNumber || '—'}</p>
                          <p className="text-sm font-medium text-gray-900 truncate max-w-[220px]">{ctx.jobOrProjectTitle || '—'}</p>
                          {ctx.job && <div className="mt-0.5"><StatusBadge status={ctx.job.status} className="text-[9px]" /></div>}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700">{ctx.customerName || '—'}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                            <span className="text-sm text-gray-800">{ctx.formType}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          {isSigned ? (
                            <span data-testid={`form-status-signed-${inst.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700">
                              <Lock className="w-2.5 h-2.5" /> Signed
                            </span>
                          ) : (
                            <span data-testid={`form-status-pending-${inst.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700">
                              <Pencil className="w-2.5 h-2.5" /> Pending
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">{date || '—'}</td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {ctx.assignees.length === 0 ? (
                            <span className="text-gray-300 italic">Unassigned</span>
                          ) : (
                            <div className="flex flex-wrap gap-0.5">
                              {ctx.assignees.slice(0, 3).map((a, i) => (
                                <span key={i} className="inline-block bg-slate-100 text-slate-700 rounded px-1.5 py-0.5 text-[10px]">{a}</span>
                              ))}
                              {ctx.assignees.length > 3 && <span className="text-gray-400">+{ctx.assignees.length - 3}</span>}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openInstance(inst); }}>
                            {isSigned ? <Eye className="w-3.5 h-3.5 text-gray-500" /> : <Pencil className="w-3.5 h-3.5 text-gray-500" />}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-2">
        {loading ? null : sorted.length === 0 ? (
          <Card className="bg-white border-gray-100">
            <CardContent className="py-10 text-center">
              <FileSignature className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm text-gray-500">No forms yet</p>
            </CardContent>
          </Card>
        ) : sorted.map(inst => {
          const ctx = resolveContext(inst);
          const displayStatus = getFormDisplayStatus(inst);
          const isSigned = displayStatus === 'signed';
          const date = (inst.signed_at || inst.filled_at || inst.created_at || '').slice(0, 10);
          return (
            <Card key={inst.id} data-testid={`form-mobile-${inst.id}`} onClick={() => openInstance(inst)} className="bg-white border-gray-100 cursor-pointer">
              <CardContent className="p-3">
                <div className="flex items-start justify-between mb-1">
                  <div className="min-w-0">
                    <p className="text-xs font-mono text-gray-500">{ctx.jobOrProjectNumber || '—'}</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{ctx.formType}</p>
                  </div>
                  {isSigned ? (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-emerald-50 text-emerald-700"><Lock className="w-2 h-2" />Signed</span>
                  ) : (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-amber-50 text-amber-700"><Pencil className="w-2 h-2" />Pending</span>
                  )}
                </div>
                <p className="text-xs text-gray-600 truncate">{ctx.customerName}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-gray-400">{date}</span>
                  {ctx.assignees[0] && <span className="text-[10px] text-gray-500 truncate ml-2">{ctx.assignees.slice(0, 2).join(', ')}</span>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
